/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
 */

import React from 'react'
import moment from 'moment'
import R from 'ramda'
import autoBind from 'react-autobind'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { FontIcon, CircularProgress, FlatButton, MenuItem, SelectField } from 'material-ui'
import { green500, pink600 } from 'material-ui/styles/colors'

import { SlidingPanel, Panel } from 'generic/panels'
import Card from 'generic/card/Card'

import Base from 'components/common/Base'
import SearchToolBar from 'components/common/prestation/SearchToolBar'
import List from 'components/common/prestation/List'
import Detail from 'components/applicant/prestation/dashboard/Detail'

import * as actions from 'actions/ApplicantPrestation'
import * as constants from 'constants/Prestation'
import * as loginConstants from 'constants/Login'
import 'resources/styles/applicant/History.css'

const VIEWPORT_HEIGHT = 675
const ELEMENT_HEIGHT = 78

class HistoryPage extends React.Component {

  constructor (props) {
    super(props)
    autoBind(this)

    this.state = {
      isScrolled: false,
      loading: false,
      detailLoading: false,
      prestations: [],
      pagination: {
        pageSize: 0,
        totalPrestations: 0,
        numberOfPrestations: 0,
        totalPages: 0
      },
      data: {}
    }
  }

  componentDidMount() {
    if (R.path(['location', 'data'], this.props.history)) {
      this.search(this.props.history.location.data)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.location !== prevProps.location) {
      this.search(this.props.history.location.data)
    }
  }

  search (data) {
    this.setState({
      loading: !this.state.loading,
      isScrolled: true,
      data: data,
    })

    const query = {
      is_search: true,
      page: R.path(['page'], data) || 1,
      sort: '-ordered_start_time',
      id: R.path(['uid'], data),
      invoice_aba_code: R.path(['invoice_aba_code'], data),
      date_gte: R.path(['date_gte'], data)
        ? R.path(['date_gte'], data)
        : R.path(['after'], data)
          ? moment(data.after).set({hours: 0, minutes: 0, seconds: 0}).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
      date_lte: R.path(['before'], data)
        ? moment(data.before).set({hours: 23, minutes: 59, seconds: 59}).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      invoice_date: R.path(['invoice_date'], data)
        ? moment(data.invoice_date).format('YYYY-MM-DD')
        : undefined,
      interpreters_in: R.length(R.path(['interpreters'], data))
        ? data.interpreters.map(interpreter => interpreter.uid).join()
        : undefined,
      paying_customers_in: R.length(R.path(['payingCustomers'], data))
        ? data.payingCustomers.map(payingCustomer => payingCustomer.uid).join()
        : undefined,
      applicant_entities_in: R.length(R.path(['applicant_entities'], data))
        ? data.applicant_entities.map(applicantEntity => applicantEntity.uid).join()
        : undefined,
      applicant_identities_in: R.length(R.path(['applicant_identities'], data))
        ? data.applicant_identities.map(applicantIdentity => applicantIdentity.uid).join()
        : undefined,
      consumer_entities_in: R.length(R.path(['consumer_entities'], data))
        ? data.consumer_entities.map(consumerEntity => consumerEntity.uid).join()
        : undefined,
      consumer_identities_in: R.length(R.path(['consumer_identities'], data))
        ? data.consumer_identities.map(consumerIdentity => consumerIdentity.first_name + ' ' + consumerIdentity.last_name).join()
        : undefined,
      beneficiary_number: R.path(['beneficiary_number'], data),
      address_in: R.path(['address'], data),
      invitations_interpreter_uid_in: R.length(R.path(['invited_interpreters'], data))
        ? data.invited_interpreters.map(interpreter => interpreter.uid).join()
        : undefined,
      languages_in: R.length(R.path(['languages'], data))
        ? data.languages.join()
        : undefined,
      preferred_nationality_origin_in: R.length(R.path(['origines'], data))
        ? data.origines.join()
        : undefined,
      prestation_type_in: R.length(R.path(['prestationTypes'], data))
        ? data.prestationTypes.join()
        : undefined,
      wkf_step_in: R.length(R.path(['workflowSteps'], data))
        ? data.workflowSteps.join()
        : undefined,
      wkf_current: R.length(R.path(['workflowSteps'], data)) > 0 || undefined,
      wkf_user_is_interpreter: R.path(['wkf_user_is_interpreter'], data),
      has_proposals: R.path(['has_proposals'], data),
      is_archived: R.path(['is_archived'], data) || false,
    }

    let prestations = []
    let pagination = this.state.pagination

    this.props.actions.setData('selectedPrestation', undefined)

    actions.getPrestations(query)
      .then((result) => {
        const objs = R.path(['data', 'objects'], result)

        prestations = objs.objects
        pagination = {
          numberOfPrestations: objs.objects_number,
          totalPrestations: objs.total_elements,
          totalPages: objs.total_pages,
          pageSize: objs.page_size,
        }

        if (objs.objects.length === 1) {
          this.props.actions.selectPrestation(objs.objects[0])
        }
      })
      .finally(() => {
        this.setState({
          loading: !this.state.loading,
          prestations: prestations,
          pagination: pagination
        })
      })
  }

  prestationPagination (page) {
    let data = {
      ...this.state.data,
      page: page,
    }

    this.search(data)
  }
  
  handleUpdatedPrestation (prestation, options={}) {
    this.setState(prev => ({
      prestations: prev.prestations.map(p =>
        p.uid !== prestation.uid ? p : { ...p, ...options }
      )
    }))

    actions.getPrestationUid(prestation.uid)
      .then((result) => {
        const obj = result.data.object

        this.setState(prev => ({
          prestations: prev.prestations.map(p =>
            p.uid !== obj.uid ? p : {
              ...obj,
              is_cancelled_lately: p.is_cancelled_lately,
              is_interpreter_proposals_refused: p.is_interpreter_proposals_refused,
              ...options,
            }
          )
        }))
      })
  }

  prestationListTitle () {
    if (!this.state.pagination.totalPrestations ||
        !this.state.pagination.numberOfPrestations ||
        this.state.loading) {
      return this.props.intl.formatMessage(constants.messages.NO_RESULTS_NUMBER)
    }

    return this.state.pagination.numberOfPrestations + ' ' +
           this.props.intl.formatMessage(constants.messages.RESULTS_NUMBER) + ' ' +
           this.state.pagination.totalPrestations
  }

  nextPrestationPage () {
    const currentPage = this.state.data.page + 1

    this.prestationPagination(currentPage)
  }

  previousPrestationPage () {
    const currentPage = this.state.data.page - 1

    this.prestationPagination(currentPage)
  }

  handleExport (event, index, value) {
    this.setState({
      statisticValue: value,
      loadingExport: true
    })

    const url = actions.exportCSV(
      this.state.pagination.totalPrestations,
      this.state.data,
      value
    )

    this.link.href = url
    this.link.click()
  }

  render () {
    const role = this.props.user.role

    return (
      <Base
        page={2}
      >
        <div className='Search'>
          <SlidingPanel
            ratio='2:3'
            isScrolled={this.state.isScrolled}
          >
            <Panel>
              <div className='navigation-bar' />
              <div />
              <SearchToolBar search={this.search}/>
            </Panel>

            <Panel>
              <div className='navigation-bar'>
                {
                  this.state.isScrolled && !R.path(['location', 'data'], this.props.history)
                    ? <FlatButton
                      onClick={() => {this.setState({isScrolled: false})}}
                      label={this.props.intl.formatMessage(constants.messages.SEARCH_ACTION_TITLE)}
                      primary
                      icon={<FontIcon className='material-icons'>keyboard_backspace</FontIcon>}
                    />
                    : null
                }
              </div>

              <Card
                title={this.props.intl.formatMessage(constants.messages.RESULTS_TITLE)}
                rightIcon={this.prestationListTitle()}
                actions={
                  !this.state.loading &&
                  this.state.prestations.length
                    ? <div>
                      <a
                        href=''
                        target='_blank'
                        style={{display: 'none'}}
                        ref={link => { this.link = link }}
                      >
                      </a>
                      <SelectField value={this.state.statisticValue}
                        style={{width: '315px'}}
                        onChange={this.handleExport}
                        hintText={this.props.intl.formatMessage(constants.messages.STATISTIC_CSV_FILE_LABEL)}
                      >

                        {
                          role !== loginConstants.INTERPRETER_ROLE
                            ? <MenuItem
                              value={constants.PRESTATION_REPORT}
                              primaryText={this.props.intl.formatMessage(constants.messages.HISTORY_EXPORT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          this.props.user.is_staff ||
                          role === loginConstants.INTERPRETER_ROLE
                            ? <MenuItem
                              value={constants.PRESTATION_REPORT_GENERAL}
                              primaryText={this.props.intl.formatMessage(constants.messages.GENERAL_EXPORT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          this.props.user.is_staff
                            ? <MenuItem
                              value={constants.PRESTATION_REPORT_STATISTIC}
                              primaryText={this.props.intl.formatMessage(constants.messages.STATISTIC_EXPORT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          this.props.user.is_staff
                            ? <MenuItem
                              value={constants.PRESTATION_REPORT_PHOTO}
                              primaryText={this.props.intl.formatMessage(constants.messages.PHOTO_EXPORT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          this.props.user.is_staff
                            ? <MenuItem
                              value={constants.PRESTATION_REPORT_CREATION_DATE}
                              primaryText={this.props.intl.formatMessage(constants.messages.CREATION_DATE_EXPORT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          this.props.user.is_staff
                            ? <MenuItem
                                value={constants.PRESTATION_REPORT_VALIDATION_DATE}
                                primaryText={this.props.intl.formatMessage(constants.messages.VALIDATION_DATE_EXPORT_CSV_LABEL)}
                                leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                              />
                            : undefined
                        }
                              
                        <MenuItem
                          value={constants.LANGUAGE_PRESTATION_REPORT}
                          primaryText={this.props.intl.formatMessage(constants.messages.HISTORY_EXPORT_LANGUAGE_CSV_LABEL)}
                          leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                        />

                        {
                          role !== loginConstants.INTERPRETER_ROLE
                            ? <MenuItem
                              value={constants.INTERPRETER_PRESTATION_REPORT}
                              primaryText={this.props.intl.formatMessage(constants.messages.HISTORY_EXPORT_INTERPRETER_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          role !== loginConstants.APPLICANT_ROLE ||
                          this.props.user.is_staff
                            ? <MenuItem
                              value={constants.APPLICANT_PRESTATION_REPORT}
                              primaryText={this.props.intl.formatMessage(constants.messages.HISTORY_EXPORT_APPLICANT_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }

                        {
                          role !== loginConstants.PAYING_CUSTOMER_ROLE
                            ? <MenuItem
                              value={constants.PAYING_CUSTOMER_PRESTATION_REPORT}
                              primaryText={this.props.intl.formatMessage(constants.messages.HISTORY_EXPORT_PAYING_CUSTOMER_CSV_LABEL)}
                              leftIcon={<FontIcon className="material-icons" color={green500}>cloud_download</FontIcon>}
                            />
                            : undefined
                        }
                      </SelectField>
                    </div>
                    : undefined
                }
              >
                {
                  this.state.loading
                    ? <div style={{textAlign: 'center', paddingTop: 100}}>
                      <CircularProgress color={pink600} size={100} thickness={5} />
                    </div>
                    : <div>
                        <div
                          style={{height: VIEWPORT_HEIGHT, overflowY: 'auto', overflowX: 'hidden', marginBottom: 10}}
                        >
                          <List
                            actions={this.props.actions}
                            height={ELEMENT_HEIGHT}
                            prestations={this.state.prestations}
                            setLoading={() => this.setState(prev => ({ detailLoading: !prev.detailLoading }))}
                            loading={this.state.detailLoading}
                          />
                        </div>

                        {
                          this.state.pagination.totalPrestations > this.state.pagination.pageSize
                          ? <div className='col-md-2 col-md-offset-10 col-xs-12'>
                            <FlatButton
                              secondary={true}
                              disabled={R.path(['page'], this.state.data) === 1}
                              icon={<FontIcon className='material-icons'>keyboard_arrow_left</FontIcon>}
                              style={{minWidth: 50}}
                              onClick={this.previousPrestationPage}
                            />
                            <span>{ R.path(['page'], this.state.data) }</span>
                            <FlatButton
                              secondary={true}
                              disabled={R.path(['page'], this.state.data) === this.state.pagination.totalPages}
                              icon={<FontIcon className='material-icons'>keyboard_arrow_right</FontIcon>}
                              style={{minWidth: 50}}
                              onClick={this.nextPrestationPage}
                            />
                          </div>
                          : undefined
                        }
                    </div>
                }
              </Card>
            </Panel>

            <Panel>
              <div className='navigation-bar' />
              <Detail
                actions={this.props.actions}
                prestation={this.props.selectedPrestation}
                handleUpdatedPrestation={this.handleUpdatedPrestation}
                loading={this.state.detailLoading}
              />
            </Panel>
          </SlidingPanel>
        </div>
      </Base>
    )
  }
}

const mapStateToProps = (state) => ({
  selectedPrestation: state.ApplicantPrestation.selectedPrestation,
  user: state.User.user,
})

const mapActionsToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch)
})

export default connect(mapStateToProps, mapActionsToProps)(injectIntl(HistoryPage))



// WEBPACK FOOTER //
// ./src/components/pages/HistoryPage.js